import React, { useEffect, useMemo, useRef, useState } from "react";
import "./AboutUsSection.css";
import { CONFIG } from "../config";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";

const getImageUrl = (val, imagesBase) => {
  if (!val || typeof val !== "string") return "";
  if (/^https?:\/\//i.test(val) || /^\/\//.test(val)) return val;
  if (val.startsWith("/")) return val;
  return `${imagesBase}/${encodeURIComponent(val)}`;
};

const toNumber = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s.replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : null;
};

function useInViewOnce(options = { threshold: 0.2 }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        obs.disconnect();
      }
    }, options);

    obs.observe(el);
    return () => obs.disconnect();
  }, [options]);

  return [ref, inView];
}

function useCountUp(target, startWhen, duration = 900) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!startWhen) return;

    const t = Number(target);
    if (!Number.isFinite(t)) return;

    let raf = 0;
    const start = performance.now();

    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setValue(Math.round(t * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, startWhen, duration]);

  return value;
}

export default function AboutUsSection({ data, fontStyles }) {
  const safe = data || {};
  const display = safe.display !== false;
  const isMobile = useIsMobile();

  // ✅ HOOKS MUST RUN ALWAYS (eslint safe)
  const IMAGES_BASE =
    CONFIG?.IMAGES_BASE ||
    "https://firebasestorage.googleapis.com/v0/b/your-project.appspot.com/o";

  const imageUrl = useMemo(
    () => getImageUrl(safe.image || "", IMAGES_BASE),
    [safe.image, IMAGES_BASE]
  );

  const [wrapRef, show] = useInViewOnce({ threshold: 0.2 });

  const yearsNum = toNumber(safe.yearsExperience);
  const projectsNum = toNumber(safe.projectsCompleted);

  const yearsAnimated = useCountUp(yearsNum ?? 0, show, 3500);
  const projectsAnimated = useCountUp(projectsNum ?? 0, show, 4500);

  // ✅ only return after hooks
  if (!display) return null;

  const title = safe.title || "";
  const subtitle = safe.subtitle || "";
  const description = safe.description || "";
  const buttonText = safe.buttonText || "";
  const buttonLink = safe.buttonLink || "#";
  const backgroundColor = safe.backgroundColor || "transparent";
  const sectionStyles = safe.sectionStyles || {};
  const sizeOverrides = sectionStyles.fontSizes || {};

  // Dynamic styles from config (optional)
  const kickerStyle = {
    fontFamily: fontStyles?.subHeading?.fontFamily || "inherit",
    fontWeight: fontStyles?.subHeading?.fontWeight || 700,
    fontSize: adjustFontSize(
      sizeOverrides.subHeading || fontStyles?.subHeading?.fontSize || undefined,
      isMobile
    ),
    lineHeight: fontStyles?.subHeading?.lineHeight || undefined,
    color: sectionStyles?.subHeadingColor || fontStyles?.subHeading?.color || "#c1a27a",
  };

  const titleStyle = {
    fontFamily: fontStyles?.heading?.fontFamily || "inherit",
    fontWeight: fontStyles?.heading?.fontWeight || 500,
    fontSize: adjustFontSize(
      sizeOverrides.heading || fontStyles?.heading?.fontSize || undefined,
      isMobile
    ),
    lineHeight: fontStyles?.heading?.lineHeight || undefined,
    color: sectionStyles?.headingColor || fontStyles?.heading?.color || "#111",
  };

  const descStyle = {
    fontFamily: fontStyles?.content?.fontFamily || "inherit",
    fontWeight: fontStyles?.content?.fontWeight || 400,
    fontSize: adjustFontSize(
      sizeOverrides.content || fontStyles?.content?.fontSize || undefined,
      isMobile
    ),
    lineHeight: fontStyles?.content?.lineHeight || undefined,
    color: sectionStyles?.contentColor || fontStyles?.content?.color || "#777",
  };

  const statNumStyle = {
    fontFamily: fontStyles?.stats?.fontFamily || "inherit",
    fontWeight: fontStyles?.stats?.fontWeight || 800,
    fontSize: adjustFontSize(
      sizeOverrides.stats || fontStyles?.stats?.fontSize || undefined,
      isMobile
    ),
    lineHeight: fontStyles?.stats?.lineHeight || undefined,
    color: sectionStyles?.statsColor || fontStyles?.stats?.color || "#111",
  };

  const buttonStyle = {
    fontFamily: fontStyles?.button?.fontFamily || "inherit",
    fontWeight: fontStyles?.button?.fontWeight || 700,
    fontSize: adjustFontSize(
      sizeOverrides.button || fontStyles?.button?.fontSize || undefined,
      isMobile
    ),
    color: sectionStyles?.buttonTextColor || "#fff",
    backgroundColor: sectionStyles?.buttonBgColor || "#111",
  };

  return (
    <section className="about-section" style={{ background: backgroundColor }}>
      <div className="about-shell" ref={wrapRef}>
        {/* LEFT: IMAGE */}
        <div className={`about-left ${show ? "show" : ""}`}>
          {imageUrl ? (
            <div
              className="about-image"
              style={{ backgroundImage: `url("${imageUrl}")` }}
              role="img"
              aria-label={title || "About image"}
            />
          ) : (
            <div className="about-image about-image--placeholder">
              <div className="about-ph">
                <div className="about-ph-emoji">🏠</div>
                <div>No image available</div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: CARD + STATS */}
        <div className={`about-right ${show ? "show" : ""}`}>
          <div className="about-card-wrap">
            <div className="about-card">
              {title ? (
              <h2 className="about-title" style={titleStyle}>
                {title}
              </h2>
            ) : null}
            {subtitle ? (
              <div className="about-kicker" style={kickerStyle}>
                {subtitle}
              </div>
            ) : null}

          

            {description ? (
              <p className="about-desc" style={descStyle}>
                {description}
              </p>
            ) : null}

            {buttonText ? (
              <a className="about-btn" href={buttonLink} style={buttonStyle}>
                {buttonText}
              </a>
            ) : null}
            </div>
          </div>

          {(safe.yearsExperience || safe.projectsCompleted) && (
            <div className="about-stats">
              {safe.yearsExperience ? (
                <div className="about-stat">
                  <div className="about-stat-num" style={statNumStyle}>
                    {yearsNum !== null ? yearsAnimated : safe.yearsExperience}
                    <span className="plus">+</span>
                  </div>
                  <div className="about-stat-label" style={descStyle}>
                    Years of Experience
                  </div>
                </div>
              ) : null}

              {safe.projectsCompleted ? (
                <div className="about-stat">
                  <div className="about-stat-num" style={statNumStyle}>
                    {projectsNum !== null ? projectsAnimated : safe.projectsCompleted}
                    <span className="plus">+</span>
                  </div>
                  <div className="about-stat-label" style={descStyle}>
                    Projects Completed
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
